import {
    AdapterRegistry,
    IModel,
    ImportExportSettings,
    Metamodel,
    SharedModel,
    SnowflakeV2Sink,
    SnowflakeV2TableDataset
} from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { Activity } from 'app/model/pipeline/activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {
    SnowflakeSinkAdapter,
    snowflakeSinkAdapter
} from 'app/model/adapters/activities/copysink/snowflake.sink.adapter';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { CopyJobActivity, ReadMethod } from 'app/model/copyjob/copy-job.activity';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SnowflakeV2Sink),
    asynchronous: true,
    adapts: {
        ...snowflakeSinkAdapter,
        writeBehavior: '*',
        upsertSettings: '*'
    }
})
export class SnowflakeV2SinkAdapter extends SnowflakeSinkAdapter {
    protected override model(): IModel {
        return SnowflakeV2Sink.MODEL;
    }

    protected override async _toResource(model: SnowflakeV2Sink): Promise<Model.SnowflakeV2Sink> {
        const result: Model.SnowflakeSink = await super._toResource(model);
        if (model.importSettings != null) {
            result.importSettings.storageIntegration =
                ValueAdapter.toResource(model.importSettings, ImportExportSettings.STORAGE_INTEGRATION) || undefined;
        }
        if (
            (
                model.owner instanceof CopyActivity &&
                (model.owner.sinkDataset as SnowflakeV2TableDataset)?.version === '1.1'
            ) ||
            (
                model.owner instanceof CopyJobActivity &&
                model.owner.readMethod === ReadMethod.SnapshotPlusIncremental
            )
        ) {
            const writeBehavior = ValueAdapter.toResource(model, SnowflakeV2Sink.WRITE_BEHAVIOR);
            if (
                writeBehavior === writeBehaviorOptionValues.upsert ||
                writeBehavior === writeBehaviorOptionValues.SCD2
            ) {
                result.writeBehavior = writeBehavior;
                this._upsertToResource(result, model);
            }
        }
        return result;
    }

    protected override async _toModel(resource: Model.SnowflakeV2Sink, owner: Activity): Promise<SnowflakeV2Sink> {
        const result = (await super._toModel(resource, owner)) as SnowflakeV2Sink;
        if (resource.importSettings != null) {
            ValueAdapter.toModel(
                result.importSettings,
                ImportExportSettings.STORAGE_INTEGRATION,
                resource.importSettings.storageIntegration
            );
        }
        ValueAdapter.toModel(result, SnowflakeV2Sink.WRITE_BEHAVIOR, resource.writeBehavior);
        if (
            resource.writeBehavior === writeBehaviorOptionValues.upsert ||
            resource.writeBehavior === writeBehaviorOptionValues.SCD2
        ) {
            this._upsertToModel(resource, result);
        }
        return result;
    }

    private _upsertToResource(result: Model.SnowflakeV2Sink, model: SnowflakeV2Sink): void {
        result.upsertSettings = {
            keys: ValueAdapter.toResource(model, SnowflakeV2Sink.WRITE_BEHAVIOR_KEYS)
        };
    }

    private _upsertToModel(resource: Model.SnowflakeV2Sink, result: SnowflakeV2Sink): void {
        // for copy-wizard generated activities, where upsertSettings parameterized as one
        if (
            resource.upsertSettings &&
            (resource.upsertSettings as SharedModel.ExpressionValue)?.type !== 'Expression'
        ) {
            const upsertSettings = resource.upsertSettings as Model.WriteBehaviorUpsertOptions;
            ValueAdapter.toModel(result, SnowflakeV2Sink.WRITE_BEHAVIOR_KEYS, upsertSettings.keys);
        }
    }
}
